import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type EyGradeSettingsData,
  type EyIndicatorFields,
  type EyReflection,
  type EyWorkspace,
  earlyYearsApi,
} from './early-years';
import { reportCardsKeys } from './hooks';

export const eyKeys = {
  levels: (periodId: string) => ['report-cards', 'ey', 'levels', periodId] as const,
  indicators: (gradeLevel: number, includeInactive = false) =>
    ['report-cards', 'ey', 'indicators', gradeLevel, includeInactive] as const,
  allIndicators: (gradeLevel: number) => ['report-cards', 'ey', 'indicators', gradeLevel] as const,
  allWorkspaces: ['report-cards', 'ey', 'workspace'] as const,
  workspace: (periodId: string, gradeLevel: number) =>
    ['report-cards', 'ey', 'workspace', periodId, gradeLevel] as const,
  settings: (periodId: string, gradeLevel: number) => ['report-cards', 'ey', 'settings', periodId, gradeLevel] as const,
};

export type EyReflectionField = keyof EyReflection;

export function useEyLevelsQuery(periodId: string | undefined) {
  return useQuery({
    queryKey: eyKeys.levels(periodId ?? ''),
    queryFn: () => earlyYearsApi.levels(periodId!),
    enabled: !!periodId,
  });
}

export function useEyIndicatorsQuery(gradeLevel: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: eyKeys.indicators(gradeLevel ?? -1, includeInactive),
    queryFn: () => earlyYearsApi.listIndicators(gradeLevel!, includeInactive),
    enabled: gradeLevel !== undefined,
  });
}

export function useEyWorkspaceQuery(periodId: string | undefined, gradeLevel: number | undefined) {
  return useQuery({
    queryKey: eyKeys.workspace(periodId ?? '', gradeLevel ?? -1),
    queryFn: () => earlyYearsApi.workspace(periodId!, gradeLevel!),
    enabled: !!periodId && gradeLevel !== undefined,
  });
}

export function useEyGradeSettingsQuery(periodId: string | undefined, gradeLevel: number | undefined) {
  return useQuery({
    queryKey: eyKeys.settings(periodId ?? '', gradeLevel ?? -1),
    queryFn: () => earlyYearsApi.getGradeSettings(periodId!, gradeLevel!),
    enabled: !!periodId && gradeLevel !== undefined,
  });
}

export function useCreateEyIndicatorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: EyIndicatorFields & { gradeLevel: number }) => earlyYearsApi.createIndicator(dto),
    onSuccess: (_data, { gradeLevel }) => queryClient.invalidateQueries({ queryKey: eyKeys.allIndicators(gradeLevel) }),
  });
}

export function useUpdateEyIndicatorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      gradeLevel: number;
      dto: Partial<EyIndicatorFields> & { active?: boolean };
    }) => earlyYearsApi.updateIndicator(id, dto),
    // The API refreshes the indicator's copies in open quarters, so open workspaces refetch too.
    onSuccess: (_data, { gradeLevel }) => {
      void queryClient.invalidateQueries({ queryKey: eyKeys.allIndicators(gradeLevel) });
      void queryClient.invalidateQueries({ queryKey: eyKeys.allWorkspaces });
    },
  });
}

export function useImportEyIndicatorsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gradeLevel, rows }: { gradeLevel: number; rows: EyIndicatorFields[] }) =>
      earlyYearsApi.importIndicators(gradeLevel, rows),
    onSuccess: (_data, { gradeLevel }) => queryClient.invalidateQueries({ queryKey: eyKeys.allIndicators(gradeLevel) }),
  });
}

export function useSetEyIndicatorsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      periodId,
      gradeLevel,
      indicatorIds,
    }: {
      periodId: string;
      gradeLevel: number;
      indicatorIds: string[];
    }) => earlyYearsApi.setIndicators(periodId, gradeLevel, indicatorIds),
    onSuccess: (workspace, { periodId, gradeLevel }) => {
      queryClient.setQueryData(eyKeys.workspace(periodId, gradeLevel), workspace);
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useSaveEySummariesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      periodId,
      gradeLevel,
      items,
    }: {
      periodId: string;
      gradeLevel: number;
      items: { title: string; body: string }[];
    }) => earlyYearsApi.saveSummaries(periodId, gradeLevel, items),
    onSuccess: (_data, { periodId, gradeLevel }) =>
      queryClient.invalidateQueries({ queryKey: eyKeys.workspace(periodId, gradeLevel) }),
  });
}

/** Patches one child in the cached workspace, so a panel that remounts shows what was saved. */
function patchChild(
  queryClient: ReturnType<typeof useQueryClient>,
  periodId: string,
  gradeLevel: number,
  cardId: string,
  patch: (child: EyWorkspace['children'][number], workspace: EyWorkspace) => Partial<EyWorkspace>
) {
  queryClient.setQueryData<EyWorkspace>(eyKeys.workspace(periodId, gradeLevel), workspace => {
    if (!workspace) return workspace;
    const child = workspace.children.find(candidate => candidate.cardId === cardId);
    return child ? { ...workspace, ...patch(child, workspace) } : workspace;
  });
}

export function useSaveEyMarkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      standardId,
      valueId,
    }: {
      periodId: string;
      gradeLevel: number;
      cardId: string;
      standardId: string;
      valueId: string | null;
    }) => earlyYearsApi.saveStandardMark(cardId, standardId, valueId),
    onSuccess: (_data, { periodId, gradeLevel, cardId, standardId, valueId }) => {
      patchChild(queryClient, periodId, gradeLevel, cardId, (child, workspace) => {
        const marks = { ...child.marks };
        if (valueId) marks[standardId] = valueId;
        else delete marks[standardId];
        const graded = new Set(workspace.gradedStandardIds);
        if (valueId) graded.add(standardId);
        return {
          children: workspace.children.map(candidate =>
            candidate.cardId === cardId ? { ...candidate, marks } : candidate
          ),
          gradedStandardIds: [...graded],
        };
      });
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useSaveEyReflectionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      field,
      value,
    }: {
      periodId: string;
      gradeLevel: number;
      cardId: string;
      field: EyReflectionField;
      value: string | null;
    }) => earlyYearsApi.saveReflection(cardId, { [field]: value }),
    onSuccess: (_data, { periodId, gradeLevel, cardId, field, value }) => {
      patchChild(queryClient, periodId, gradeLevel, cardId, (_child, workspace) => ({
        children: workspace.children.map(candidate =>
          candidate.cardId === cardId ? { ...candidate, [field]: value } : candidate
        ),
      }));
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useSaveEyGradeSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, gradeLevel, dto }: { periodId: string; gradeLevel: number; dto: EyGradeSettingsData }) =>
      earlyYearsApi.saveGradeSettings(periodId, gradeLevel, dto),
    onSuccess: (settings, { periodId, gradeLevel }) => {
      queryClient.setQueryData(eyKeys.settings(periodId, gradeLevel), settings);
      void queryClient.invalidateQueries({ queryKey: eyKeys.workspace(periodId, gradeLevel) });
    },
  });
}
